import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, ArrowRight, Volume2, Layers, Scissors } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * WelcomeTour — a 30-second guided intro for brand-new learners.
 *
 * The tour is a modal that walks the user through the three phonics
 * touchpoints in order:
 *   1. SoundStrip  — tap letters to hear sounds
 *   2. Blending    — sounds → word
 *   3. Segmenting  — word → sounds
 *
 * Only shown once — visibility is gated on `user.onboarded_at` from the
 * server, and dismissing (or skipping, or clicking "Take me there") PUTs
 * /api/settings/onboarded so it never comes back.
 */
const STEPS = [
    {
        icon: Volume2,
        eyebrow: "Step 1 · Hear the sounds",
        title: "Tap a letter to hear its sound.",
        body: "The letter 'd' doesn't say dee here. It says /d/. Get familiar with the sounds first — everything else builds on this.",
    },
    {
        icon: Layers,
        eyebrow: "Step 2 · Blend them",
        title: "Slide sounds together into a word.",
        body: "Three sounds — b · a · t — become one word. Blending is how you decode a word you've never seen.",
    },
    {
        icon: Scissors,
        eyebrow: "Step 3 · Pull them apart",
        title: "Break a word back into its sounds.",
        body: "Hear cat, then tap the three sounds in order. This is the opposite of blending — and just as important.",
    },
];

export default function WelcomeTour() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [dismissing, setDismissing] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!user) return;
        // Show only for learners who haven't finished the tour yet.
        if (!user.onboarded_at) setVisible(true);
    }, [user]);

    if (!visible) return null;

    const markSeen = async () => {
        setDismissing(true);
        try {
            await api.put("/settings/onboarded");
            refresh?.();
        } catch {
            /* onboarded flag is best-effort */
        }
        setVisible(false);
    };

    const finish = async (goToPhonics) => {
        await markSeen();
        if (goToPhonics) navigate("/app/curriculum/phonics");
    };

    const current = STEPS[step];
    const isLast = step === STEPS.length - 1;
    const StepIcon = current.icon;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 fade-up"
            style={{ background: "rgba(28, 35, 33, 0.55)", backdropFilter: "blur(6px)" }}
            data-testid="welcome-tour"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
                style={{ borderTop: "6px solid #2D5D4E" }}
            >
                <button
                    onClick={() => finish(false)}
                    disabled={dismissing}
                    data-testid="welcome-close"
                    aria-label="Skip tour"
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#819888] hover:text-[#1C2321] hover:bg-[#F5F1E4] transition-colors"
                >
                    <X className="w-4 h-4" strokeWidth={1.8} />
                </button>

                <div className="p-8 md:p-10">
                    <p className="label-eyebrow mb-2" data-testid="welcome-eyebrow">{current.eyebrow}</p>
                    <div className="w-14 h-14 rounded-xl bg-[#E0EDE6] flex items-center justify-center mb-5">
                        <StepIcon className="w-6 h-6 text-[#2D5D4E]" strokeWidth={1.6} />
                    </div>
                    <h2
                        className="text-3xl font-medium text-[#1C2321] mb-3 leading-tight"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="welcome-title"
                    >
                        {current.title}
                    </h2>
                    <p className="text-[#515B57] leading-relaxed mb-8" data-testid="welcome-body">
                        {current.body}
                    </p>

                    {/* Progress dots */}
                    <div className="flex items-center gap-2 mb-6" data-testid="welcome-progress">
                        {STEPS.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === step
                                        ? "w-8 bg-[#2D5D4E]"
                                        : i < step
                                            ? "w-4 bg-[#2D5D4E]"
                                            : "w-4 bg-[#E5E7E2]"
                                }`}
                            />
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <button
                            onClick={() => finish(false)}
                            disabled={dismissing}
                            data-testid="welcome-skip"
                            className="text-sm text-[#515B57] hover:text-[#1C2321] font-medium"
                        >
                            Skip for now
                        </button>
                        {isLast ? (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => finish(false)}
                                    disabled={dismissing}
                                    data-testid="welcome-done"
                                    className="px-5 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#E8E2D2]"
                                >
                                    I&apos;ll explore
                                </button>
                                <Link
                                    to="/app/curriculum/phonics"
                                    onClick={() => markSeen()}
                                    data-testid="welcome-go-phonics"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                                >
                                    Take me there
                                    <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                                </Link>
                            </div>
                        ) : (
                            <button
                                onClick={() => setStep((s) => s + 1)}
                                data-testid="welcome-next"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                            >
                                Next
                                <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
